import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setPreferences } from "@/lib/prefs";
import { Loader2, ChefHat } from "lucide-react";
import { toast } from "sonner";
import { carryDemoDishIntoAccount } from "@/lib/carryDish";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        // Include the referral code if the user came in via ?ref=CODE on Landing.
        const refCode = (localStorage.getItem("cs_ref") || "").trim().toUpperCase();
        const { data } = await api.post("/auth/google/session", {
          session_id,
          ...(refCode ? { referral_code: refCode } : {}),
        });
        setPreferences(data?.preferences || {});
        // Clear the referral cookie once we've successfully attached it.
        if (refCode) localStorage.removeItem("cs_ref");
        await refresh();
        // Strip the fragment
        window.history.replaceState({}, "", window.location.pathname);
        // Carry any Landing-simulator customisation into the account. Existing
        // Google sign-ins won't have customised data in localStorage so this
        // is a safe no-op for returning users.
        const carried = await carryDemoDishIntoAccount();
        if (carried) {
          toast.success(`Welcome, ${data.name || data.email}. Your dish "${carried.name}" is in your recipes.`);
          navigate(carried.id ? `/recipes/${carried.id}?tour=1` : "/recipes", { replace: true });
        } else {
          toast.success(`Welcome, ${data.name || data.email}.`);
          navigate("/dashboard", { replace: true });
        }
      } catch (e) {
        toast.error("Google sign-in failed. Please try again.");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center flex-col gap-4" data-testid="auth-callback">
      <img
        src="/brand/mark-256.png"
        alt="CulinaireSpecialist"
        className="w-12 h-12 rounded-full object-contain"
        style={{ background: "#0a0806", boxShadow: "0 0 0 1px rgba(232,207,154,0.35)" }}
      />
      <Loader2 className="w-5 h-5 animate-spin gold" />
      <p className="text-sm text-muted-foreground">Finishing sign-in…</p>
    </div>
  );
}
